import React, { useState, useEffect } from 'react'
import {MessageCircleDashed as MessageCircle, ThumbsUp, Reply, Send} from 'lucide-react'
import { useAuth } from '../context/AuthContext'

interface Comment {
  id: string
  contentId: string
  userId: string
  username: string
  avatar: string
  text: string
  timestamp: string
  likes: number
  likedBy: string[]
  replies: Reply[]
}

interface Reply {
  id: string
  userId: string
  username: string
  avatar: string
  text: string
  timestamp: string
  likes: number
  likedBy: string[]
}

interface CommentSectionProps {
  contentId: string
  onAuthRequired: () => void
}

export function CommentSection({ contentId, onAuthRequired }: CommentSectionProps) {
  const { user } = useAuth()
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')

  useEffect(() => {
    const storedComments = JSON.parse(localStorage.getItem('magicCartoonComments') || '[]')
    const contentComments = storedComments.filter((c: Comment) => c.contentId === contentId)
    setComments(contentComments)
  }, [contentId])

  const saveComments = (updatedComments: Comment[]) => {
    const allComments = JSON.parse(localStorage.getItem('magicCartoonComments') || '[]')
    const otherComments = allComments.filter((c: Comment) => c.contentId !== contentId)
    localStorage.setItem('magicCartoonComments', JSON.stringify([...otherComments, ...updatedComments]))
    setComments(updatedComments)
  }

  const handleAddComment = () => {
    if (!user) {
      onAuthRequired()
      return
    }

    if (!newComment.trim()) return

    const comment: Comment = {
      id: Date.now().toString(),
      contentId,
      userId: user.id,
      username: user.username,
      avatar: user.avatar,
      text: newComment,
      timestamp: new Date().toISOString(),
      likes: 0,
      likedBy: [],
      replies: []
    }

    const updatedComments = [...comments, comment]
    saveComments(updatedComments)
    setNewComment('')
  }

  const handleAddReply = (commentId: string) => {
    if (!user) {
      onAuthRequired()
      return
    }

    if (!replyText.trim()) return

    const reply: Reply = {
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      avatar: user.avatar,
      text: replyText,
      timestamp: new Date().toISOString(),
      likes: 0,
      likedBy: []
    }

    const updatedComments = comments.map(c => 
      c.id === commentId 
        ? { ...c, replies: [...c.replies, reply] }
        : c
    )

    saveComments(updatedComments)
    setReplyText('')
    setReplyingTo(null)
  }

  const handleLikeComment = (commentId: string) => {
    if (!user) {
      onAuthRequired()
      return
    }

    const updatedComments = comments.map(c => {
      if (c.id === commentId) {
        const hasLiked = c.likedBy.includes(user.id)
        return {
          ...c,
          likes: hasLiked ? c.likes - 1 : c.likes + 1,
          likedBy: hasLiked 
            ? c.likedBy.filter(id => id !== user.id)
            : [...c.likedBy, user.id]
        }
      }
      return c
    })

    saveComments(updatedComments)
  }

  const handleLikeReply = (commentId: string, replyId: string) => {
    if (!user) {
      onAuthRequired()
      return
    }

    const updatedComments = comments.map(c => {
      if (c.id === commentId) {
        return {
          ...c,
          replies: c.replies.map(r => {
            if (r.id === replyId) {
              const hasLiked = r.likedBy.includes(user.id)
              return {
                ...r,
                likes: hasLiked ? r.likes - 1 : r.likes + 1,
                likedBy: hasLiked
                  ? r.likedBy.filter(id => id !== user.id)
                  : [...r.likedBy, user.id]
              }
            }
            return r
          })
        }
      }
      return c
    })

    saveComments(updatedComments)
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-6">
        <MessageCircle className="w-6 h-6 text-pink-500" />
        <h3 className="text-2xl font-bold text-gray-800">
          Discussion ({comments.length})
        </h3>
      </div>

      {/* Add Comment */}
      <div className="mb-8">
        <div className="flex gap-3">
          {user && (
            <img
              src={user.avatar}
              alt={user.username}
              className="w-10 h-10 rounded-full"
            />
          )}
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={user ? "Share your thoughts..." : "Log in to join the discussion"}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none resize-none"
              rows={3}
              disabled={!user}
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleAddComment}
                disabled={!user || !newComment.trim()}
                className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Comment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-6">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="border-b border-gray-100 last:border-0 pb-6">
              <div className="flex gap-3">
                <img
                  src={comment.avatar}
                  alt={comment.username}
                  className="w-10 h-10 rounded-full"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-800">{comment.username}</span>
                    <span className="text-sm text-gray-500">{formatTimestamp(comment.timestamp)}</span>
                  </div>
                  <p className="text-gray-700 mb-3">{comment.text}</p>
                  
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleLikeComment(comment.id)}
                      className={`flex items-center gap-1 text-sm transition-colors ${
                        user && comment.likedBy.includes(user.id)
                          ? 'text-pink-500'
                          : 'text-gray-500 hover:text-pink-500'
                      }`}
                    >
                      <ThumbsUp className="w-4 h-4" />
                      {comment.likes > 0 && comment.likes}
                    </button>
                    <button
                      onClick={() => setReplyingTo(comment.id)}
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-pink-500 transition-colors"
                    >
                      <Reply className="w-4 h-4" />
                      Reply
                    </button>
                  </div>

                  {/* Replies */}
                  {comment.replies.length > 0 && (
                    <div className="mt-4 space-y-4 pl-8 border-l-2 border-gray-200">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="flex gap-3">
                          <img
                            src={reply.avatar}
                            alt={reply.username}
                            className="w-8 h-8 rounded-full"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-800 text-sm">{reply.username}</span>
                              <span className="text-xs text-gray-500">{formatTimestamp(reply.timestamp)}</span>
                            </div>
                            <p className="text-gray-700 text-sm mb-2">{reply.text}</p>
                            <button
                              onClick={() => handleLikeReply(comment.id, reply.id)}
                              className={`flex items-center gap-1 text-xs transition-colors ${
                                user && reply.likedBy.includes(user.id)
                                  ? 'text-pink-500'
                                  : 'text-gray-500 hover:text-pink-500'
                              }`}
                            >
                              <ThumbsUp className="w-3 h-3" />
                              {reply.likes > 0 && reply.likes}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply Input */}
                  {replyingTo === comment.id && (
                    <div className="mt-4 pl-8">
                      <div className="flex gap-3">
                        {user && (
                          <img
                            src={user.avatar}
                            alt={user.username}
                            className="w-8 h-8 rounded-full"
                          />
                        )}
                        <div className="flex-1">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write a reply..."
                            className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none resize-none text-sm"
                            rows={2}
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              onClick={() => {
                                setReplyingTo(null)
                                setReplyText('')
                              }}
                              className="px-4 py-1 text-sm text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleAddReply(comment.id)}
                              disabled={!replyText.trim()}
                              className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Reply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
